import { httpGet } from 'utils';

interface PortalDocumentEnvelope<T> {
  data: T[];
  totalRecords: number;
  totalResults: number;
}

// ─── cultwineratings — ratings per wine, POST with body filter ───────────────

export interface CwWineRatingRecord {
  lwin11: string;
  wineName?: string;
  countryName?: string;
  regionName?: string;
  region?: string;
  subRegion?: string;
  vintage?: number;
  vintageScore?: number | null;
  wa?: number | null;
  vinous?: number | null;
  combinedCriticScore?: number | null;
  normalizedCriticScore?: number | null;
  fairValueScore?: number | null;
  performanceScore?: number | null;
  liquidityScore?: number | null;
  grade?: number | null;
  totalScore?: number | null;
  inAum?: boolean;
  forSale?: boolean;
  // Market prices
  winesearcherMarketPrice?: number | null;
  livExMarketPrice?: number | null;
  // Fair value calculations
  fvLx?: number | null;
  fvWs?: number | null;
  fvBackupModel?: number | null;
  valuePct?: number | null;
  valuePctClipped?: number | null;
  // Historical prices - Liv-Ex
  lxPrice6m?: number | null;
  lxPrice3y?: number | null;
  lxPrice5y?: number | null;
  // Historical prices - Wine Searcher
  wsPrice6m?: number | null;
  wsPrice3y?: number | null;
  wsPrice5y?: number | null;
  // Performance metrics
  perfSourceUsed?: string | null;
  perf6m?: number | null;
  perf1y?: number | null;
  perf3y?: number | null;
  perf5y?: number | null;
  allTimeRange?: number | null;
  weightedPerf?: number | null;
  lwin7Perf?: number | null;
  finalPerfScore?: number | null;
  finalPerfScoreClipped?: number | null;
  // Liquidity components
  scoreProducerVolumePerYear?: number | null;
  scoreLwin7QuantityPerYear?: number | null;
  scoreMarketCapPerYear?: number | null;
}

export async function fetchWineRatings(lwin11s: string[]): Promise<CwWineRatingRecord[]> {
  if (lwin11s.length === 0) return [];
  const body = {
    docType: 'cultwineratings',
    page: 0,
    pageSize: Math.max(lwin11s.length, 10),
    lwin11: lwin11s.join(','),
  };
  const res = await httpGet<
    PortalDocumentEnvelope<CwWineRatingRecord>,
    { docType: string; lwin11: string; page: number; pageSize: number }
  >('portal/PortalDocument', body);
  return res.data;
}

// ─── wine_scores — tasting notes + drinking window per wine, GET with query ──

interface WineScoreRecord {
  lwin11: string;
  vReviewer?: string | null;
  vReviewDate?: string | null;
  vDrinkFrom?: string | number | null;
  vDrinkTo?: string | number | null;
  vTastingNote?: string | null;
  vScoreMedian?: number | null;
  vExternalLink?: string | null;
  waCriticFullname?: string | null;
  waScoreDate?: string | null;
  waDrinkYearMin?: string | number | null;
  waDrinkYearMax?: string | number | null;
  waTastingNote?: string | null;
  waScore?: number | null;
  waArticleUrl?: string | null;
}

export async function fetchWineScores(lwin11: string): Promise<WineScoreRecord | null> {
  if (!lwin11) return null;
  const res = await httpGet<
    PortalDocumentEnvelope<WineScoreRecord>,
    { docType: string; lwin11: string; page: number; pageSize: number }
  >('portal/PortalDocument', {
    docType: 'wine_scores',
    lwin11,
    page: 0,
    pageSize: 10,
  });
  return res?.data?.[0] ?? null;
}

// ─── ai_explainer — AI-generated wine explainer per wine ───────────────────────

interface AiExplainerRecord {
  lwin11: string;
  explainer: string;
}

interface AiExplainerEnvelope {
  data: AiExplainerRecord[];
  count: number;
}

export async function fetchAiExplainer(lwin11: string): Promise<AiExplainerRecord | null> {
  if (!lwin11) return null;
  const res = await httpGet<AiExplainerEnvelope, { docType: string; docRef: string }>('portal/PortalDocument', {
    docType: 'ai_explainer',
    docRef: lwin11,
  });
  return res?.data?.[0] ?? null;
}

// ─── transaction_history — trade history per wine ──────────────────────────────

interface TransactionHistoryRecord {
  dealDate: string;
  lwin11: string;
  price: number;
  unitSize: string;
  vintage: number;
  wineName: string;
  quantity: number;
}

interface TransactionHistoryResponse {
  totalPages: number;
  totalRecords: number;
  lastMonth: number;
  lastWeek: number;
  lastYear: number;
  lwin11: string;
  vintage: number;
  wineName: string;
  results: TransactionHistoryRecord[];
}

export async function fetchTransactionHistory(
  lwin11: string,
  options?: { sortBy?: string; orderBy?: string; limit?: number; offset?: number },
): Promise<TransactionHistoryResponse | null> {
  if (!lwin11) return null;
  const res = await httpGet<
    TransactionHistoryResponse,
    {
      docType: string;
      lwin11: string;
      sortBy: string;
      orderBy: string;
      limit: number;
      offset: number;
      isLoggedIn: boolean;
    }
  >('portal/PortalDocument', {
    docType: 'transaction_history',
    lwin11,
    sortBy: options?.sortBy ?? 'date',
    orderBy: options?.orderBy ?? 'desc',
    limit: options?.limit ?? 10,
    offset: options?.offset ?? 1,
    isLoggedIn: true,
  });
  return res ?? null;
}

// ─── latest_critic_scores — critic scores and tasting notes ────────────────────

interface LatestCriticScoresRecord {
  lwin: string;
  vReviewer: string | null;
  vReviewDate: string | null;
  vDrinkFrom: string | null;
  vDrinkTo: string | null;
  vScoreMedian: number | null;
  vTastingNote: string | null;
  vExternalLink: string | null;
  vIngestionDate: string | null;
  waCriticFullname: string | null;
  waScoreDate: string | null;
  waDrinkYearMin: string | null;
  waDrinkYearMax: string | null;
  waScore: number | null;
  waTastingNote: string | null;
  waArticleUrl: string | null;
  waIngestionDate: string | null;
  cruCriticName: string | null;
  cruReviewer: string | null;
  cruReviewDate: string | null;
  cruDrinkFrom: string | null;
  cruDrinkTo: string | null;
  cruScore: number | null;
  cruTastingNote: string | null;
}

export async function fetchLatestCriticScores(lwin11: string): Promise<LatestCriticScoresRecord | null> {
  if (!lwin11) return null;
  const res = await httpGet<PortalDocumentEnvelope<LatestCriticScoresRecord>, { docType: string; lwin11: string }>(
    'portal/PortalDocument',
    { docType: 'latest_critic_scores', lwin11 },
  );
  return res?.data?.[0] ?? null;
}

// ─── producer_notes_latest — producer notes from critics ───────────────────────

interface ProducerNotesRecord {
  producerName: string;
  reviewer: string;
  reviewDate: string;
  producerNote: string;
  source: string;
}

export async function fetchProducerNotes(producerName: string): Promise<ProducerNotesRecord | null> {
  if (!producerName) return null;
  const res = await httpGet<PortalDocumentEnvelope<ProducerNotesRecord>, { docType: string; producerName: string }>(
    'portal/PortalDocument',
    { docType: 'producer_notes_latest', producerName },
  );
  return res?.data?.[0] ?? null;
}
